import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { editorToolbarPluginKey } from '@atlaskit/editor-plugin-toolbar/plugin-key';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';

import type { RemixModalMetadata } from '../types';

export type MalleableUiPluginState = {
	/** localIds of MAUI embeds that were just replaced by an AI edit and are loading. */
	aiEditReplacementLocalIds: Set<string>;
	/**
	 * Variant-picker updates handled FE-side by `useUpdateRemix`/`useOnComplete`.
	 * The rovo-bridge update processors check this set to skip the BE-pushed
	 * duplicate insert. Key is `historyEntry.fieldId` (image) or the MAUI app
	 * id from `historyEntry.url` (MAUI); the two id spaces don't collide.
	 */
	inFlightVariantUpdates: Set<string>;
	isRemixModalOpen: boolean;
	remixModalMetadata?: RemixModalMetadata;
	remixSource?: string;
	showLocationPicker: boolean;
};

export type MalleableUiPluginMeta = {
	/** Add a localId to the AI edit replacement set. */
	addAiEditReplacementLocalId?: string;
	/** Add an id to the in-flight variant updates set (toolbar variant click). */
	addInFlightVariantUpdateId?: string;
	isRemixModalOpen?: boolean;
	remixModalMetadata?: RemixModalMetadata;
	remixSource?: string;
	/** Remove a localId from the AI edit replacement set (on embed load). */
	removeAiEditReplacementLocalId?: string;
	/** Remove an id from the in-flight variant updates set (useOnComplete done). */
	removeInFlightVariantUpdateId?: string;
	showLocationPicker?: boolean;
};

export const malleableUiPluginKey: PluginKey<MalleableUiPluginState> =
	new PluginKey<MalleableUiPluginState>('malleableUiPlugin');

export function createToolbarSuppressionPlugin(): SafePlugin {
	return new SafePlugin({
		filterTransaction(tr, state) {
			// Block shouldShowToolbar:true transactions when the remix modal is open.
			// The mouseup listener on view.root fires even for clicks inside the remix
			// modal portal, which would re-show the toolbar. Filter those out.
			const toolbarMeta = tr.getMeta(editorToolbarPluginKey);
			if (toolbarMeta?.shouldShowToolbar === true) {
				const malleableState = malleableUiPluginKey.getState(state);
				if (malleableState?.isRemixModalOpen) {
					return false;
				}
			}
			return true;
		},
	});
}

export function createMalleableUiPmPlugin(dispatch: Dispatch): SafePlugin {
	return new SafePlugin({
		key: malleableUiPluginKey,
		state: {
			init(): MalleableUiPluginState {
				return {
					isRemixModalOpen: false,
					remixModalMetadata: undefined,
					showLocationPicker: false,
					aiEditReplacementLocalIds: new Set(),
					inFlightVariantUpdates: new Set(),
				};
			},
			apply(tr, pluginState): MalleableUiPluginState {
				const meta: MalleableUiPluginMeta | undefined = tr.getMeta(malleableUiPluginKey);

				if (!meta) {
					return pluginState;
				}

				let changed = false;
				let nextIds = pluginState.aiEditReplacementLocalIds;

				if (meta.addAiEditReplacementLocalId && !nextIds.has(meta.addAiEditReplacementLocalId)) {
					nextIds = new Set(nextIds);
					nextIds.add(meta.addAiEditReplacementLocalId);
					changed = true;
				}

				if (
					meta.removeAiEditReplacementLocalId &&
					nextIds.has(meta.removeAiEditReplacementLocalId)
				) {
					nextIds = new Set(nextIds);
					nextIds.delete(meta.removeAiEditReplacementLocalId);
					changed = true;
				}

				let nextInFlightVariantUpdates = pluginState.inFlightVariantUpdates;

				if (
					meta.addInFlightVariantUpdateId &&
					!nextInFlightVariantUpdates.has(meta.addInFlightVariantUpdateId)
				) {
					nextInFlightVariantUpdates = new Set(nextInFlightVariantUpdates);
					nextInFlightVariantUpdates.add(meta.addInFlightVariantUpdateId);
					changed = true;
				}

				if (
					meta.removeInFlightVariantUpdateId &&
					nextInFlightVariantUpdates.has(meta.removeInFlightVariantUpdateId)
				) {
					nextInFlightVariantUpdates = new Set(nextInFlightVariantUpdates);
					nextInFlightVariantUpdates.delete(meta.removeInFlightVariantUpdateId);
					changed = true;
				}

				if (
					meta.isRemixModalOpen !== undefined ||
					changed ||
					(meta.showLocationPicker !== undefined &&
						fg('platform_editor_ai_remix_in_editor_loading'))
				) {
					const result: MalleableUiPluginState = {
						isRemixModalOpen: meta.isRemixModalOpen ?? pluginState.isRemixModalOpen,
						remixModalMetadata:
							'remixModalMetadata' in meta
								? meta.remixModalMetadata
								: pluginState.remixModalMetadata,
						remixSource: 'remixSource' in meta ? meta.remixSource : pluginState.remixSource,
						showLocationPicker:
							meta.showLocationPicker !== undefined &&
							fg('platform_editor_ai_remix_in_editor_loading')
								? meta.showLocationPicker
								: pluginState.showLocationPicker,
						aiEditReplacementLocalIds: nextIds,
						inFlightVariantUpdates: nextInFlightVariantUpdates,
					};
					dispatch(malleableUiPluginKey, result);
					return result;
				}

				return pluginState;
			},
		},
	});
}
